<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import Papa from 'papaparse'
import { Upload, Download, Pencil, ShieldCheck, ClipboardPlus } from 'lucide-vue-next'
import Column from 'primevue/column'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import PageHeader from '@lib/components/shared/PageHeader.vue'
import DataTable from '@lib/components/shared/DataTable.vue'
import EmptyState from '@lib/components/shared/EmptyState.vue'
import SkeletonRows from '@lib/components/shared/SkeletonRows.vue'
import ExpandRowPanel from '@lib/components/shared/ExpandRowPanel.vue'
import BulkActionBar from '@lib/components/shared/BulkActionBar.vue'
import CursorPagination from '@lib/components/shared/CursorPagination.vue'
import BBtn from '@lib/components/base/BBtn.vue'
import BBadge from '@lib/components/base/BBadge.vue'
import RowActions from '@lib/components/shared/RowActions.vue'
import { useTicketsList, type TicketFilters } from '@/composables/useTicketsList'
import { useEventOptions, useTicketsetOptions } from '@/composables/useLookups'
import { useFilters, type FilterParser } from '@lib/composables/useFilters'
import type { LocationQueryValue } from 'vue-router'
import { useFormatters } from '@lib/composables/useFormatters'
import { useToast } from '@lib/composables/useToast'
import { useConfirm } from '@lib/composables/useConfirm'
import { useUiStore } from '@/stores/ui'
import { useAuthStore } from '@/stores/auth'
import { useClipboardStore } from '@/stores/clipboard'
import { sdk } from '@/graphql/client'
import { relayIdToInt } from '@/lib/relayId'
import TicketsFilterBar from '@/components/domain/tickets/TicketsFilterBar.vue'
import TicketEditPanel from '@/components/domain/tickets/TicketEditPanel.vue'
import TicketMobileCard from '@/components/domain/tickets/TicketMobileCard.vue'

const { t } = useI18n()
const router = useRouter()
const ui = useUiStore()
const auth = useAuthStore()
const clipboard = useClipboardStore()
const toast = useToast()
const { confirm } = useConfirm()
const fmt = useFormatters()

// Filter shape: matches TicketFilters from useTicketsList (F5 part A).
// useFilters parsers (below) ensure URL string→typed roundtrip for
// boolean/nullable fields (e.g. `?used=true` parses back to boolean true,
// not the string "true").
const initialFilters: TicketFilters = {
    search: '',
    event: null,
    ticketset: null,
    used: null,
    code: '',
    email: '',
    firstName: '',
    lastName: '',
    orderBy: null
}

function asString(raw: LocationQueryValue | LocationQueryValue[] | undefined): string {
    if (Array.isArray(raw)) return raw[0] ?? ''
    return raw ?? ''
}

function asNullableString(raw: LocationQueryValue | LocationQueryValue[] | undefined): string | null {
    const v = asString(raw)
    return v === '' ? null : v
}

function asNullableBool(raw: LocationQueryValue | LocationQueryValue[] | undefined): boolean | null {
    const v = asString(raw)
    if (v === 'true') return true
    if (v === 'false') return false
    return null
}

const parsers: { [K in keyof TicketFilters]?: FilterParser<TicketFilters[K]> } = {
    event: asNullableString,
    ticketset: asNullableString,
    used: asNullableBool,
    orderBy: asNullableString
}

const { filters } = useFilters<TicketFilters>(initialFilters, { parsers })

const { tickets, loading, pagination, refetch } = useTicketsList(filters)

const eventOptions = useEventOptions()
const ticketsetOptions = useTicketsetOptions()

// When an event is selected, the ticketset filter only offers ticketsets from
// that event; otherwise all ticketsets. Keeps the two filters consistent.
const filteredTicketsetOptions = computed(() => {
    const eventId = filters.value.event
    if (!eventId) return ticketsetOptions.value
    return ticketsetOptions.value.filter(ts => ts.eventId === eventId)
})

// If the selected ticketset no longer belongs to the (newly) selected event,
// clear the ticketset filter so it can't stay stuck on a hidden option.
watch(
    () => filters.value.event,
    () => {
        const current = filters.value.ticketset
        if (!current) return
        const stillValid = filteredTicketsetOptions.value.some(ts => ts.id === current)
        if (!stillValid) filters.value = { ...filters.value, ticketset: null }
    }
)

type TicketRow = (typeof tickets.value)[number]

const expandedId = ref<string | null>(null)
const selected = ref<TicketRow[]>([])

// PrimeVue native row expansion uses a `Record<rowKey, boolean>`. Derive
// it from our single-expand `expandedId` so only one row is expanded
// at a time AND the expansion renders inline beneath the row.
const expandedRows = computed<Record<string, boolean>>(() => {
    if (expandedId.value === null) return {}
    return { [expandedId.value]: true }
})

// Both desktop (md+) and mobile (< md) branches render in the DOM (only one
// is visible per Tailwind hidden classes). Each branch has its own template
// ref to avoid v-for collisions; activeEditRef picks whichever is currently
// mounted with a panel inside. See EventsView.vue (commit 3a40bae).
type EditPanelHandle = { isDirty: () => boolean }
const editPanelRefDesktop = ref<EditPanelHandle | EditPanelHandle[] | null>(null)
const editPanelRefMobile = ref<EditPanelHandle | EditPanelHandle[] | null>(null)
function unwrap(r: EditPanelHandle | EditPanelHandle[] | null): EditPanelHandle | null {
    if (!r) return null
    if (Array.isArray(r)) return r.find(x => x != null) ?? null
    return r
}
function activeEditRef(): EditPanelHandle | null {
    return unwrap(editPanelRefMobile.value) ?? unwrap(editPanelRefDesktop.value)
}
function safeIsDirty(): boolean {
    try {
        return activeEditRef()?.isDirty() ?? false
    } catch {
        return false
    }
}

onMounted(() => {
    ui.setBreadcrumbs([{ label: t('tickets.list.title') }])
})

// Used (Boolean) badge variants:
//   used=true  → 'arch'  (greyed out — already used)
//   used=false → 'ok'    (green — still valid)
function getUsedVariant(used?: boolean | null): 'ok' | 'arch' {
    return used ? 'arch' : 'ok'
}

function getUsedLabel(used?: boolean | null): string {
    return t(used ? 'tickets.usedLabel.used' : 'tickets.usedLabel.unused')
}

async function toggleExpand(id: string) {
    if (expandedId.value === id) {
        if (safeIsDirty()) {
            const ok = await confirm({
                title: t('tickets.edit.unsavedChanges'),
                severity: 'danger'
            })
            if (!ok) return
        }
        expandedId.value = null
    } else {
        expandedId.value = id
    }
}

function onRowClick(row: TicketRow) {
    if (row?.id != null) toggleExpand(String(row.id))
}

function clearFilters() {
    filters.value = { ...initialFilters }
}

// IDs in `TicketCardFragment` are Relay Global IDs (base64 of "TypeName:<int>"),
// but the backend `delete(id: Int!)`/`deleteMulti(ids: [Int]!)` expects raw
// integers. Decode at the boundary.

async function onBulkDelete() {
    if (selected.value.length === 0) return
    const ok = await confirm({
        title: t('tickets.confirm.deleteBulkTitle'),
        message: t('tickets.confirm.deleteBulkMessage', { n: selected.value.length }),
        severity: 'danger'
    })
    if (!ok) return

    const ids = selected.value.map(row => relayIdToInt(row?.id)).filter((v): v is number => v !== null)

    if (ids.length === 0) {
        toast.error(t('common.errors.generic'))
        return
    }

    let deleted = 0
    try {
        const r = await sdk.AdminDeleteTicketsMulti({ ids })
        if (r.admin?.ticket?.deleteMulti?.ok) {
            deleted = ids.length
        }
    } catch (e) {
        toast.error((e as Error).message)
        return
    }
    toast.success(t('tickets.toasts.bulkDeleted', { n: deleted }))
    selected.value = []
    refetch()
}

function onBulkAddToClipboard() {
    let added = 0
    for (const row of selected.value) {
        if (row?.code) {
            clipboard.addTicket(row.code)
            added++
        }
    }
    toast.success(t('tickets.toasts.bulkAddedToClipboard', { n: added }))
    selected.value = []
}

// Client-side CSV export (F5 deviation #4):
//   Backend has no export mutation — we serialise the *current page* of
//   tickets via papaparse and trigger a blob download. Toast wording is
//   honest about the per-page scope.
function onExport() {
    const rows = tickets.value.map(t => ({
        id: t.id,
        code: t.code,
        used: t.used,
        usedAt: t.usedAt ?? '',
        firstName: t.firstName ?? '',
        lastName: t.lastName ?? '',
        email: t.email ?? '',
        phone: t.phone ?? '',
        boughtAt: t.boughtAt ?? '',
        event: t.ticketset?.event?.name ?? '',
        ticketset: t.ticketset?.name ?? ''
    }))
    const csv = Papa.unparse(rows)
    const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
    a.download = `tickets-${stamp}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    toast.success(t('tickets.toasts.exportedPage', { n: rows.length }))
}

const isEmpty = computed(() => !loading.value && tickets.value.length === 0)

// T48: Close expand-row + clear selection when active instance changes.
// `auth.invalidationKey` is bumped by `auth.changeInstance` — useTicketsList
// already watches it for refetch; here we additionally reset local UI state.
watch(
    () => auth.invalidationKey,
    () => {
        expandedId.value = null
        selected.value = []
    }
)
</script>

<template>
    <DefaultLayout>
        <PageHeader :title="t('tickets.list.title')">
            <template #actions>
                <BBtn type="button" @click="router.push('/tickets/verify')">
                    <ShieldCheck class="w-4 h-4" />
                    <span>{{ t('tickets.verify.verify') }}</span>
                </BBtn>
                <BBtn severity="secondary" type="button" @click="onExport">
                    <Download class="w-4 h-4" />
                    <span>{{ t('tickets.actions.export') }}</span>
                </BBtn>
                <BBtn severity="secondary" type="button" @click="router.push('/tickets/import')">
                    <Upload class="w-4 h-4" />
                    <span>{{ t('tickets.actions.import') }}</span>
                </BBtn>
            </template>
        </PageHeader>

        <TicketsFilterBar
            v-model="filters"
            :event-options="eventOptions"
            :ticketset-options="filteredTicketsetOptions"
            @clear="clearFilters"
        />

        <BulkActionBar :selected-count="selected.length" @clear="selected = []">
            <BBtn
                severity="secondary"
                size="small"
                type="button"
                :label="t('tickets.bulk.addToClipboard')"
                @click="onBulkAddToClipboard"
            />
            <BBtn
                severity="danger"
                size="small"
                type="button"
                :label="t('tickets.bulk.deleteAll')"
                @click="onBulkDelete"
            />
        </BulkActionBar>

        <div class="bg-white border border-gray-200 rounded-12 overflow-hidden">
            <SkeletonRows v-if="loading && tickets.length === 0" :rows="8" :columns="7" />
            <EmptyState
                v-else-if="isEmpty"
                :title="t('tickets.list.empty.title')"
                :subtitle="t('tickets.list.empty.subtitle')"
            >
                <template #cta>
                    <BBtn @click="router.push('/tickets/import')">{{ t('tickets.list.empty.cta') }}</BBtn>
                </template>
            </EmptyState>
            <template v-else>
                <!--
                    Both branches render in the DOM (visibility gated by
                    hidden/md:block/md:hidden). Each owns its own
                    TicketEditPanel ref to avoid template-ref collisions
                    across two trees.
                -->
                <!-- DESKTOP (md+): table -->
                <div class="hidden md:block">
                    <DataTable
                        :value="tickets"
                        row-key="id"
                        :selection="selected"
                        :expanded-rows="expandedRows"
                        @update:selection="v => (selected = v)"
                        @row-click="onRowClick"
                    >
                        <Column selection-mode="multiple" :style="{ width: '38px' }" />
                        <Column :header="t('tickets.list.columns.code')" :style="{ width: '140px' }">
                            <template #body="{ data }">
                                <span class="font-mono text-xs">{{ data.code }}</span>
                            </template>
                        </Column>
                        <Column :header="t('tickets.list.columns.used')" :style="{ width: '110px' }">
                            <template #body="{ data }">
                                <BBadge :variant="getUsedVariant(data.used)" :label="getUsedLabel(data.used)" />
                            </template>
                        </Column>
                        <Column :header="t('tickets.list.columns.buyer')">
                            <template #body="{ data }">
                                <div class="text-sm">
                                    {{ [data.firstName, data.lastName].filter(Boolean).join(' ') || '—' }}
                                </div>
                                <div class="text-xs text-gray-500">{{ data.email ?? '' }}</div>
                            </template>
                        </Column>
                        <Column :header="t('tickets.list.columns.event')">
                            <template #body="{ data }">
                                <span class="text-sm">{{ data.ticketset?.event?.name ?? '—' }}</span>
                            </template>
                        </Column>
                        <Column :header="t('tickets.list.columns.ticketset')">
                            <template #body="{ data }">
                                <span class="text-sm">{{ data.ticketset?.name ?? '—' }}</span>
                            </template>
                        </Column>
                        <Column :header="t('tickets.list.columns.boughtAt')" :style="{ width: '140px' }">
                            <template #body="{ data }">
                                <span class="text-xs tabular-nums">{{ fmt.dateTime(data.boughtAt) }}</span>
                            </template>
                        </Column>
                        <Column :header="t('tickets.list.columns.usedAt')" :style="{ width: '140px' }">
                            <template #body="{ data }">
                                <span class="text-xs tabular-nums">{{ fmt.dateTime(data.usedAt) }}</span>
                            </template>
                        </Column>
                        <Column :style="{ width: '60px' }">
                            <template #body="{ data }">
                                <RowActions
                                    :actions="[
                                        { key: 'clipboard', icon: ClipboardPlus, label: t('tickets.actions.addToClipboard') },
                                        { key: 'verify', icon: ShieldCheck, label: t('tickets.verify.verify') },
                                        { key: 'edit', icon: Pencil, label: t('common.actions.edit') }
                                    ]"
                                    @action="
                                        k => {
                                            if (k === 'edit') toggleExpand(String(data.id))
                                            else if (k === 'verify')
                                                router.push({ path: '/tickets/verify', query: { code: data.code } })
                                            else if (k === 'clipboard') {
                                                clipboard.addTicket(data.code)
                                                toast.success(t('tickets.toasts.addedToClipboard'))
                                            }
                                        }
                                    "
                                />
                            </template>
                        </Column>
                        <template #expansion="{ data }">
                            <ExpandRowPanel
                                :title="
                                    t('tickets.edit.titleEdit', {
                                        code: data.code ?? ''
                                    })
                                "
                                @close="toggleExpand(String(data.id))"
                            >
                                <TicketEditPanel
                                    ref="editPanelRefDesktop"
                                    :ticket="data"
                                    @saved="
                                        () => {
                                            refetch()
                                            expandedId = null
                                        }
                                    "
                                    @deleted="
                                        () => {
                                            refetch()
                                            expandedId = null
                                        }
                                    "
                                    @cancel="() => (expandedId = null)"
                                />
                            </ExpandRowPanel>
                        </template>
                    </DataTable>
                </div>
                <!-- MOBILE (< md): vertical card list -->
                <ul class="md:hidden divide-y divide-gray-100">
                    <li v-for="row in tickets" :key="row.id" class="p-2">
                        <TicketMobileCard
                            :ticket="row"
                            :selected="selected.some(s => s.id === row.id)"
                            :expanded="expandedId === String(row.id)"
                            @toggle-select="
                                selected = selected.some(s => s.id === row.id)
                                    ? selected.filter(s => s.id !== row.id)
                                    : [...selected, row]
                            "
                            @toggle-expand="toggleExpand(String(row.id))"
                            @add-to-clipboard="
                                () => {
                                    clipboard.addTicket(row.code)
                                    toast.success(t('tickets.toasts.addedToClipboard'))
                                }
                            "
                        />
                        <ExpandRowPanel
                            v-if="expandedId === String(row.id)"
                            :title="t('tickets.edit.titleEdit', { code: row.code ?? '' })"
                            class="mt-2"
                            @close="toggleExpand(String(row.id))"
                        >
                            <TicketEditPanel
                                ref="editPanelRefMobile"
                                :ticket="row"
                                @saved="
                                    () => {
                                        refetch()
                                        expandedId = null
                                    }
                                "
                                @deleted="
                                    () => {
                                        refetch()
                                        expandedId = null
                                    }
                                "
                                @cancel="() => (expandedId = null)"
                            />
                        </ExpandRowPanel>
                    </li>
                </ul>

                <CursorPagination
                    :page-size="pagination.pageSize.value"
                    :has-prev="pagination.canGoPrev.value"
                    :has-next="pagination.canGoNext.value"
                    :page-index="pagination.currentPageIndex.value"
                    :range-start="pagination.rangeStart.value"
                    :range-end="pagination.rangeEnd.value"
                    :total-count="pagination.totalCount.value"
                    @prev="pagination.prevPage"
                    @next="pagination.nextPage"
                    @update:page-size="pagination.setPageSize"
                />
            </template>
        </div>
    </DefaultLayout>
</template>
